import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import imageCompression from 'browser-image-compression';

function VerifyInvitationInfoConfirm() {
  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [message, setMessage] = useState('');

  const [profile, setProfile] = useState({
    name: '',
    last_name: '',
    phone_number: '',
    email: '',
    profile_image: '',
  });

  const {
    data: dataProfile,
    isLoading: isLoadingProfile,
    isError: isErrorProfile,
  } = useQuery({
    queryKey: ['userProfileDetails'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/register-user-details-api`, {
        withCredentials: true,
      });
      return res.data;
    },
    staleTime: 0,
    cacheTime: Infinity,
  });

  useEffect(() => {
    if (dataProfile) {
      if (dataProfile.Status === 'Success') {
        const user = dataProfile.user;
        setProfile({
          name: user.name || '',
          last_name: user.last_name || '',
          phone_number: user.phone_number || '',
          email: user.email || '',
          profile_image: user.profile_image || '',
        });
      } else {
        setMessage('Failed to load user data');
      }
    }
  }, [dataProfile]);

  const [previewImage, setPreviewImage] = useState(null);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };

      const compressedBlob = await imageCompression(file, options);
      const compressedFile = new File([compressedBlob], file.name, {
        type: compressedBlob.type,
        lastModified: Date.now(),
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(compressedFile);

      setProfile((prev) => ({
        ...prev,
        profile_image: compressedFile,
      }));
    } catch (error) {
      console.error('Image compression failed:', error);
      toast.error('Image upload failed. Please try again.');
    }
  };

  const [values, setValues] = useState({
    password: '',
    confirm_password: '',
  });

  const [phoneMessage, setPhoneMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'password' || name === 'confirm_password') {
      setValues((prev) => ({ ...prev, [name]: value }));
    } else if (name === 'phone_number') {
      handlePhoneChange(e);
    } else {
      setProfile((prev) => ({ ...prev, [name]: value }));
    }
  };

  const compressTo100KB = async (file) => {
    let quality = 0.9;
    let compressed = file;

    while (true) {
      const options = {
        maxSizeMB: 0.1,
        initialQuality: quality,
        maxWidthOrHeight: 600,
        useWebWorker: true,
      };

      compressed = await imageCompression(compressed, options);

      if (compressed.size / 1024 <= 100 || quality <= 0.1) break;

      quality -= 0.1;
    }

    return new File([compressed], file.name, {
      type: compressed.type,
      lastModified: Date.now(),
    });
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    if (/\s/.test(value)) {
      setPhoneMessage('⚠️ Spaces are not allowed. They were removed automatically.');
    } else {
      setPhoneMessage('');
    }

    value = value.replace(/\s+/g, '');

    if (!/^[0-9+]*$/.test(value)) return;

    if (/^7\d{8}$/.test(value)) {
      value = '0' + value;
      setPhoneMessage('ℹ️ Added leading 0 automatically.');
    }

    if (value.length === 1 && value !== '0' && value !== '+') return;

    const digitsCount = value.replace(/\D/g, '').length;
    if (digitsCount > 15) return;

    setProfile((prev) => ({
      ...prev,
      phone_number: value,
    }));
  };

  // ── Password strength ──────────────────────────────────────────
  const getStrength = (value) => {
    let score = 0;
    if (value.length >= 8) score++;
    if (value.length >= 12) score++;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score++;
    if (/[0-9]/.test(value)) score++;
    if (/[^A-Za-z0-9]/.test(value)) score++;
    return Math.min(score, 4);
  };

  const strengthColors = { 1: '#E24B4A', 2: '#ff6600', 3: '#F0AD00', 4: '#184A2C' };
  const strengthLabels = { 0: '—', 1: 'Weak', 2: 'Fair', 3: 'Good', 4: 'Strong' };

  const pw = values.password;
  const confirmPw = values.confirm_password;
  const score = pw.length > 0 ? getStrength(pw) : 0;
  const activeColor = strengthColors[score] || '#e0e0e0';

  // ── Match state ────────────────────────────────────────────────
  const showMatch = confirmPw.length > 0;
  const passwordsMatch = pw === confirmPw;

  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const pwTrim = values.password.trim();
    const cpwTrim = values.confirm_password.trim();

    if (!pwTrim || !cpwTrim) {
      toast.error('Please fill both password fields');
      return;
    }

    if (pwTrim !== cpwTrim) {
      toast.error('Passwords do not match');
      return;
    }

    setIsUpdating(true);

    try {
      const formData = new FormData();
      formData.append('name', profile.name);
      formData.append('last_name', profile.last_name);
      formData.append('phone_number', profile.phone_number);
      formData.append('email', profile.email);

      if (pwTrim) {
        formData.append('password', pwTrim);
      }

      if (profile.profile_image instanceof File) {
        const finalImage = await compressTo100KB(profile.profile_image);
        formData.append('profile_image', finalImage);
      }

      const res = await axios.put(
        `${API_URL}/update-invited-user-profile-api`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (res.data.success) {
        toast.success('Profile updated successfully');
        queryClient.invalidateQueries(['checkInvitation']);
        navigate('/home');
      } else {
        toast.error(res.data.message || 'Update failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="scrollPageContainer theme-light" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="main-wrapper">
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />

        <ToastContainer />

        <div
          className="page-wrapper mb-4"
          style={{
            background: 'linear-gradient(135deg, #f4f7f5 0%, #e8f0ea 100%)',
            minHeight: '100vh',
            

          }}
        >
          <div className="content container-fluid">
            <div className="row justify-content-center">
              <div className="col-12 col-lg-8 col-xl-7">

                <div
                  style={{
                    background: '#ffffff',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #184A2C 0%, #0a5520 100%)',
                      padding: '1.75rem 1.5rem 1.5rem',
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                      <div>
                        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: '0.25rem' }}>
                          Your Info
                        </h5>
                        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', margin: 0 }}>
                          Finish setting up your account
                        </p>
                      </div>

                      <div
                        className="px-3 py-2"
                        style={{
                          background: 'rgba(255,255,255,0.15)',
                          borderRadius: '999px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          border: '1px solid rgba(255,255,255,0.25)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Step 2 of 2
                      </div>
                    </div>

                    
                  </div>

                  {/* Body */}
                  <div className="p-3 p-md-4" style={{ paddingBottom: 28 }}>
                    <form onSubmit={handleSubmit}>
                      {/* Avatar */}
                      <div className="text-center mb-3">
                        <div
                          className="position-relative d-inline-block"
                          style={{ cursor: 'pointer' }}
                          onClick={() => document.getElementById('uploadImageInput').click()}
                        >
                          <div
                            style={{
                              width: 100,
                              height: 100,
                              borderRadius: '50%',
                              overflow: 'hidden',
                              border: '3px solid #e5ede8',
                              background: '#f8fcf9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {previewImage || profile.profile_image ? (
                              <img
                                src={previewImage || `${API_URL}/images/${profile.profile_image}`}
                                alt="Profile"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <i className="fas fa-user" style={{ fontSize: '32px', color: '#184A2C' }}></i>
                            )}
                          </div>

                          <span
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#0a5520',
                              borderRadius: '50%',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                            }}
                          >
                            <i className="fas fa-camera" style={{ color: '#fff', fontSize: '13px' }}></i>
                          </span>

                          <input
                            id="uploadImageInput"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            style={{ display: 'none' }}
                          />
                        </div>
                      </div>

                      {/* Name */}
                      <div className="row">
                        <div className="col-6 col-md-6">
                          <label style={labelStyle}>First Name</label>
                          <input
                            type="text"
                            name="name"
                            value={profile.name}
                            onChange={handleChange}
                            placeholder="First Name"
                            className="form-control mb-3"
                            style={inputStyle}
                          />
                        </div>
                        <div className="col-6 col-md-6">
                          <label style={labelStyle}>Last Name</label>
                          <input
                            type="text"
                            name="last_name"
                            value={profile.last_name}
                            onChange={handleChange}
                            placeholder="Last Name"
                            className="form-control mb-3"
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      {/* Phone / Email */}
                      <div className="row">
                        <div className="col-6 col-md-6">
                          <label style={labelStyle}>Your Phone Number</label>
                          <input
                            type="text"
                            name="phone_number"
                            value={profile.phone_number}
                            onChange={handleChange}
                            placeholder="Phone Number"
                            className="form-control mb-1"
                            style={inputStyle}
                          />
                          {phoneMessage && (
                            <small className="d-block mb-2" style={{ color: '#ff6600', fontSize: '0.7rem' }}>
                              {phoneMessage}
                            </small>
                          )}
                        </div>
                        <div className="col-6 col-md-6">
                          <label style={labelStyle}>Your Email</label>
                          <input
                            type="email"
                            name="email"
                            readOnly
                            value={profile.email}
                            onChange={handleChange}
                            placeholder="Email"
                            className="form-control mb-3"
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      {/* Passwords */}
                      <div className="row">
                        <div className="col-6 col-md-6">
                          <label style={labelStyle}>New Password</label>
                          <input
                            type="password"
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            placeholder="New Password"
                            className="form-control mb-2"
                            style={inputStyle}
                          />
                        </div>
                        <div className="col-6 col-md-6">
                          <label style={labelStyle}>Confirm Password</label>
                          <input
                            type="password"
                            name="confirm_password"
                            value={values.confirm_password}
                            onChange={handleChange}
                            placeholder="Confirm Password"
                            className="form-control mb-2"
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      {/* Strength bar */}
                      <div style={{ marginBottom: '0.3rem' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '4px',
                          }}
                        >
                          <span style={{ fontSize: '12px', color: '#184A2C' }}>Password strength</span>
                          <span
                            style={{
                              fontSize: '12px',
                              fontWeight: 500,
                              color: pw.length > 0 ? activeColor : '#bbb',
                            }}
                          >
                            {strengthLabels[score]}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {[1, 2, 3, 4].map((i) => (
                            <div
                              key={i}
                              style={{
                                flex: 1,
                                height: '5px',
                                borderRadius: '999px',
                                background: i <= score ? activeColor : '#e5ede8',
                                transition: 'background 0.25s',
                              }}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Match feedback */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px', 
                          opacity: showMatch ? 1 : 0,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        <i
                          className={`fas ${passwordsMatch ? 'fa-check-circle' : 'fa-times-circle'}`}
                          style={{
                            fontSize: '14px',
                            color: passwordsMatch ? '#184A2C' : '#e24b4a',
                          }}
                        ></i>
                        <span
                          style={{
                            fontSize: '12px',
                            color: passwordsMatch ? '#184A2C' : '#e24b4a',
                          }}
                        >
                          {passwordsMatch ? 'Passwords match' : "Passwords don't match"}
                        </span>
                      </div>

                     

                      {/* Actions */}
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(-1)}
                          disabled={isUpdating}
                          style={{
                            padding: '0.7rem 1.4rem',
                            backgroundColor: 'transparent',
                            color: '#184A2C',
                            border: '1.5px solid #184A2C',
                            borderRadius: '40px',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: isUpdating ? 'not-allowed' : 'pointer',
                            opacity: isUpdating ? 0.6 : 1,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <i className="fas fa-arrow-left me-2"></i>
                          Back
                        </button>

                        <button
                          type="submit"
                          className='btn btn-primary'
                          disabled={isUpdating}
                          style={{
                            flex: 1,
                            maxWidth: 220,
                            padding: '0.7rem 1.4rem',
                            
                            border: 'none',
                            borderRadius: '40px',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: '#fff',
                            cursor: isUpdating ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                          }}
                           
                        >
                          {isUpdating ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                style={{ width: '0.8rem', height: '0.8rem' }}
                              ></span>
                              Updating...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-check me-2"></i>
                              Update
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                   
                </div>

                {/*<div style={{marginTop: '20%'}}></div>*/}


              </div>
            </div>
          </div>
        </div>
      </div>

       
    </div>
  );
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#184A2C',
  marginBottom: '0.3rem',
  display: 'block',
};

const inputStyle = {
  borderRadius: '12px',
  border: '1px solid #e5ede8',
  padding: '0.6rem 0.9rem',
  fontSize: '0.85rem',
  background: '#f8fcf9',
};

export default VerifyInvitationInfoConfirm;